import { useEffect, useState, useRef } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import useAuthStore from "../../store/authStore";
import Logo from "../../components/Logo";
import ThemeToggle from "../../components/ThemeToggle";
import api from "../../api/client";

export default function EmployerVerifyEmail() {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState("loading");
  const token = searchParams.get("token");
  const calledRef = useRef(false);
  const navigate = useNavigate();
  const { employerToken, employerUser, setEmployerAuth } = useAuthStore();

  useEffect(() => {
    document.title = "Joborigo — Employer Portal";
  }, []);

  useEffect(() => {
    if (!token) {
      setStatus("invalid");
      return;
    }
    if (calledRef.current) return;
    calledRef.current = true;

    api
      .get(`/employer/auth/verify-email?token=${token}`)
      .then(async () => {
        // Refresh employer data in store if logged in
        if (employerToken) {
          try {
            const { data } = await api.get("/employer/me", {
              headers: { Authorization: `Bearer ${employerToken}` },
            });
            setEmployerAuth(employerToken, data);
          } catch {}
        }
        setStatus("success");
      })
      .catch((err) => {
        console.error(
          "Employer verify error:",
          err.response?.status,
          err.response?.data,
        );
        setStatus("error");
      });
  }, [token]);

  return (
    <div className="min-h-screen bg-brand-light-bg dark:bg-brand-dark-bg flex flex-col">
      <nav className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-white/10">
        <Logo className="h-12" />
        <ThemeToggle />
      </nav>

      <div className="flex-1 flex items-center justify-center px-4">
        <div className="bg-white dark:bg-brand-dark-surface rounded-2xl border border-gray-100 dark:border-white/10 p-8 w-full max-w-sm text-center">
          <Logo className="h-12 mx-auto mb-4" />
          <span className="text-xs font-medium text-brand-violet dark:text-brand-violet-on-dark uppercase tracking-widest">
            Employer portal
          </span>

          {status === "loading" && (
            <p className="text-gray-400 mt-4">Verifying your email...</p>
          )}

          {status === "success" && (
            <>
              <div className="w-12 h-12 bg-green-100 dark:bg-green-900/20 rounded-full flex items-center justify-center mx-auto mt-4 mb-4">
                <svg
                  className="w-6 h-6 text-green-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
              <h1 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                Email verified!
              </h1>
              <p className="text-gray-400 text-sm mb-6">
                Your employer account is now active. You can access your
                dashboard.
              </p>
              <Link
                to="/employers/dashboard"
                className="block bg-brand-violet text-white py-2.5 rounded-lg font-medium hover:opacity-90 transition"
              >
                Go to dashboard
              </Link>
            </>
          )}

          {(status === "error" || status === "invalid") && (
            <>
              <div className="w-12 h-12 bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center mx-auto mt-4 mb-4">
                <svg
                  className="w-6 h-6 text-red-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </div>
              <h1 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                Link expired or invalid
              </h1>
              <p className="text-gray-400 text-sm mb-6">
                Request a new verification email from the login page.
              </p>
              <Link
                to="/employers/login"
                className="block border border-brand-violet dark:border-brand-violet-on-dark text-brand-violet dark:text-brand-violet-on-dark py-2.5 rounded-lg font-medium hover:bg-brand-violet-tint dark:hover:bg-brand-violet/10 transition"
              >
                Back to login
              </Link>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
